import {decryptData} from '../../utils/crypto';

export const LOOKUP_LIMIT = 25;
export const LOOKUP_FALLBACK_LIMIT = 100;

// The backend has no dedicated "recent" entity_reference - reference stays
// one of current/other/named - so "the recent backup" travels as an
// ordinary named lookup carrying this sentinel query. A resolver that wants
// "newest first" instead of "closest text match" checks for it up front.
export const RECENT_QUERY_TOKEN = '__recent__';

// The interpreter is a real model working from a few-shot example, not a
// fixed lookup table - "latest"/"newest"/"last" produce the same shape by
// analogy ("__latest__", "__newest__", ...) rather than always the literal
// "__recent__" the example showed. Recognizing the whole family here is
// cheaper and more reliable than trying to pin the model to one exact token.
const RECENT_QUERY_PATTERN = /^__(?:recent|latest|newest|last)__$/i;

/**
 * Whether a resolver query names the "recent"/"latest" pseudo-target rather
 * than real search text.
 * @param {*} query resolver query parameter
 * @return {boolean} true when this is the recent-request sentinel
 */
export function isRecentQuery(query) {
	return RECENT_QUERY_PATTERN.test(String(query || '').trim());
}

/**
 * Pick the newest row by a Created At-style timestamp field.
 *
 * Ties, and rows with an unparsable or missing timestamp, keep the
 * candidate found first - so "recent" over rows the API already returned
 * newest-first is not scrambled by a bad date value.
 *
 * @param {Array<object>} rows raw Canvas rows (not agent candidates)
 * @param {string} [dateField] the row's timestamp field
 * @return {object|null} the newest row, or null when rows is empty
 */
export function mostRecentRow(rows, dateField = 'created_at') {
	if (!Array.isArray(rows) || !rows.length) return null;
	let best = null;
	let bestTime = -Infinity;
	rows.forEach((row) => {
		const raw = row?.[dateField];
		const time = raw ? new Date(raw).getTime() : NaN;
		const comparable = Number.isFinite(time) ? time : -Infinity;
		if (best === null || comparable > bestTime) {
			best = row;
			bestTime = comparable;
		}
	});
	return best;
}

/**
 * Decode the Canvas API envelope.
 * @param {*} data API response data
 * @return {*} decoded response
 */
export function decodeAgentResponse(data) {
	const decoded = decryptData(data);
	return typeof decoded === 'string' ? JSON.parse(decoded) : decoded;
}

/**
 * Normalize an optional API scalar.
 * @param {*} value API value
 * @return {string|null} normalized text
 */
export function optionalText(value) {
	if (value == null) return null;
	const text = String(value).trim();
	return text || null;
}

/**
 * Read rows from supported Canvas response envelopes.
 * @param {*} document decoded API response
 * @return {Array} response rows
 */
export function responseRows(document) {
	if (Array.isArray(document)) return document;
	return document?.results ?? document?.data ?? [];
}

/**
 * Read rows from a Canvas envelope that names its collection.
 *
 * The Switch ACL and Port Range APIs nest the list one level deeper than
 * everything else - {status, data: {ipv4AclProfileConfs: [...]}} - so
 * responseRows stops at `data` and hands back that wrapper object rather
 * than an array. Naming the collection is the only way to reach the rows.
 *
 * @param {*} document decoded API response
 * @param {string} collection key holding the rows
 * @return {Array} response rows
 */
export function namedResponseRows(document, collection) {
	const rows = document?.data?.[collection];
	return Array.isArray(rows) ? rows : [];
}

/**
 * Normalize a lookup value without changing the underlying entity name.
 * @param {*} value lookup value
 * @return {string} comparable value
 */
export function normalizeLookupValue(value) {
	return String(value || '')
		.normalize('NFKC')
		.toLocaleLowerCase()
		.trim()
		.replace(/\s+/g, ' ');
}

/**
 * Levenshtein distance between two short strings.
 * @param {string} left first value
 * @param {string} right second value
 * @return {number} edit distance
 */
function editDistance(left, right) {
	if (left === right) return 0;
	if (!left) return right.length;
	if (!right) return left.length;
	let previous = Array.from({length: right.length + 1}, (_, i) => i);
	for (let i = 1; i <= left.length; i += 1) {
		const current = [i];
		for (let j = 1; j <= right.length; j += 1) {
			current[j] = Math.min(
				previous[j] + 1,
				current[j - 1] + 1,
				previous[j - 1] + (left[i - 1] === right[j - 1] ? 0 : 1),
			);
		}
		previous = current;
	}
	return previous[right.length];
}

/**
 * Normalized similarity in [0, 1] between a query and a candidate value.
 * @param {string} query user-entered query
 * @param {string} value candidate value
 * @return {number} similarity score
 */
export function lookupSimilarity(query, value) {
	const left = normalizeLookupValue(query);
	const right = normalizeLookupValue(value);
	if (!left || !right) return 0;
	if (left === right) return 1;
	const longest = Math.max(left.length, right.length);
	return 1 - editDistance(left, right) / longest;
}

/**
 * Lookup text with spaces removed so "loggedin" can match "logged in".
 * @param {*} value lookup value
 * @return {string} compacted comparable value
 */
function compactLookupValue(value) {
	return normalizeLookupValue(value).replace(/\s+/g, '');
}

/**
 * Rank exact, prefix, then substring candidate matches.
 *
 * `options.fuzzy` adds a final near-miss tier for typo tolerance. It is opt-in
 * and off by default so ordinary lookups keep their existing precision - only
 * the not-found recovery path asks for it, where showing "did you mean" beats
 * showing nothing.
 *
 * @param {Array<object>} candidates normalized entity candidates
 * @param {string} query user-entered query
 * @param {number} limit maximum results
 * @param {Function} valuesForCandidate values eligible for matching
 * @param {object} options ranking options
 * @param {boolean} options.fuzzy include near-miss matches
 * @param {number} options.fuzzyThreshold minimum similarity for a near miss
 * @return {Array<object>} matching candidates
 */
export function rankLookupCandidates(
	candidates,
	query,
	limit = LOOKUP_LIMIT,
	valuesForCandidate = (candidate) => [candidate?.name],
	options = {},
) {
	const expected = normalizeLookupValue(query);
	if (!expected) return [];
	const compactExpected = compactLookupValue(query);
	const compactOk = compactExpected.length >= 4;
	const fuzzy = options.fuzzy === true;
	const threshold = typeof options.fuzzyThreshold === 'number'
		? options.fuzzyThreshold
		: 0.6;
	return candidates
		.map((candidate, index) => {
			const values = valuesForCandidate(candidate) || [];
			const normalized = values.map(normalizeLookupValue).filter(Boolean);
			const ranks = normalized
				.map((value) => value === expected
					? 0
					: value.startsWith(expected)
						? 1
						: value.includes(expected)
							|| (compactOk && compactLookupValue(value).includes(
								compactExpected,
							))
							? 2
							: null,
				)
				.filter((rank) => rank !== null);
			if (ranks.length) {
				return {candidate, index, rank: Math.min(...ranks), score: 0};
			}
			if (!fuzzy) return {candidate, index, rank: null, score: 0};
			const score = normalized.reduce(
				(best, value) => Math.max(best, lookupSimilarity(expected, value)),
				0,
			);
			return score >= threshold
				? {candidate, index, rank: 3, score}
				: {candidate, index, rank: null, score};
		})
		.filter((item) => item.rank !== null)
		.sort((left, right) => (
			left.rank - right.rank ||
			right.score - left.score ||
			left.index - right.index
		))
		.slice(0, limit)
		// How closely each one matched travels with it. A single substring or
		// near-miss hit looks identical to an exact one by the time it reaches
		// the agent, which then acts on it as though the user had named it -
		// "sdfgh" resolving to "...qwertyuiopasdfghjklzxcvbnm..." and opening
		// its form. The rank lets the agent ask instead.
		.map((item) => ({...item.candidate, match_rank: item.rank}));
}

/**
 * Resolve candidates with a bounded unfiltered fallback when server search fails.
 * @param {object} options resolution options
 * @param {string} options.query target query
 * @param {Function} options.loadRows Canvas row loader
 * @param {Function} options.normalizeRow Canvas row normalizer
 * @param {boolean} [options.fuzzy] include near-miss matches in the fallback pass
 * @return {Promise<{rows: Array<object>, candidates: Array<object>}>} result
 */
export async function resolveCandidatesWithFallback({
	query,
	loadRows,
	normalizeRow,
	fuzzy = false,
}) {
	let rows = await loadRows(query, LOOKUP_LIMIT);
	let candidates = rankLookupCandidates(
		rows.map(normalizeRow).filter(Boolean),
		query,
	);
	if (!candidates.length) {
		rows = await loadRows(null, LOOKUP_FALLBACK_LIMIT);
		const pool = rows.map(normalizeRow).filter(Boolean);
		candidates = rankLookupCandidates(pool, query);
		// Only reach for near misses once an exact/prefix/substring pass over
		// the wider pool has already come back empty.
		if (!candidates.length && fuzzy) {
			candidates = rankLookupCandidates(
				pool,
				query,
				LOOKUP_LIMIT,
				undefined,
				{fuzzy: true},
			);
		}
	}
	return {rows, candidates};
}
